import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api.js';

// ── Query keys ────────────────────────────────────────────────────────────────

const PROVISIONS_KEY = ['provisions'];

// ── Invalidation commune ──────────────────────────────────────────────────────

function invalidateAll(qc, provisionId) {
  qc.invalidateQueries({ queryKey: ['provisions'] });
  qc.invalidateQueries({ queryKey: ['debts'] });
  qc.invalidateQueries({ queryKey: ['cash-flow'] });
  qc.invalidateQueries({ queryKey: ['forecasts', 'tree'] });
  if (provisionId) {
    qc.invalidateQueries({ queryKey: ['provisions', provisionId, 'state'] });
    qc.invalidateQueries({ queryKey: ['provisions', provisionId, 'movements'] });
  }
}

// ── Queries ───────────────────────────────────────────────────────────────────

/**
 * Liste toutes les provisions, avec filtres optionnels.
 * filters : { status?, debt_id? }
 */
export function useProvisions(filters = {}) {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.debt_id) params.set('debt_id', filters.debt_id);
  const qs = params.toString() ? `?${params.toString()}` : '';

  return useQuery({
    queryKey: [...PROVISIONS_KEY, filters],
    queryFn: () => api.get(`/provisions${qs}`),
    staleTime: 30_000,
  });
}

/**
 * Détail d'une provision (incluant debt_id, bank_account_id, target_amount_cents, target_date).
 */
export function useProvision(id) {
  return useQuery({
    queryKey: ['provisions', id],
    queryFn: () => api.get(`/provisions/${id}`),
    staleTime: 30_000,
    enabled: Boolean(id),
  });
}

/**
 * État d'une provision : funded_cents, drawn_cents, balance_cents, health.
 * Réexporte depuis useProvisionState pour centraliser les imports.
 */
export function useProvisionState(id) {
  return useQuery({
    queryKey: ['provisions', id, 'state'],
    queryFn: () => api.get(`/provisions/${id}/state`),
    staleTime: 30_000,
    enabled: Boolean(id),
  });
}

// ── Mutations ─────────────────────────────────────────────────────────────────

/**
 * Crée une nouvelle provision.
 *
 * Body minimal : { name, category_id, funding_cents_monthly, start_date }
 * Optionnels   : { end_date, cap_soft_cents, cap_hard_cents, target_amount_cents,
 *                  target_date, vat_rate, notes, debt_id, bank_account_id }
 */
export function useCreateProvision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post('/provisions', body),
    onSuccess: (data) => invalidateAll(qc, data?.id),
  });
}

/**
 * Met à jour une provision existante.
 * Payload : { id, ...champs à modifier }
 * Permet de lier/délier debt_id et bank_account_id après coup.
 */
export function useUpdateProvision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }) => api.patch(`/provisions/${id}`, body),
    onSuccess: (data, { id }) => invalidateAll(qc, id ?? data?.id),
  });
}
